import { useRef, useState } from "react";
import { AppointmentJoin } from "@/repositories/appointments.repository";
import { toast } from "sonner";
import { appointment_status } from "@/models/appointment.model";
import { assignAppointment, unassignAppointment } from "@/services/appointment-assignment.service";
import { fieldActionMessage } from "@/lib/appointment-field-actions";
import { assignmentChangesNothing, isUnassignable, type ResourceAssignment } from "@/lib/appointment-assignment";

interface UseAppointmentActionsProps {
  appointment: AppointmentJoin;
  onAppointmentUpdate?: (updatedAppointment: AppointmentJoin) => void;
}

export function useAppointmentActions({ appointment, onAppointmentUpdate }: UseAppointmentActionsProps) {
  const [assigningResource, setAssigningResource] = useState(false);

  const handleAssignResource = async (assignment: ResourceAssignment) => {
    setAssigningResource(true);
    try {
      const changesNothing = assignmentChangesNothing("vendor", assignment, appointment);
      const updatedAppointment = await assignAppointment(appointment.id, assignment);
      onAppointmentUpdate?.(updatedAppointment);
      if (changesNothing) toast.info("No changes");
      else toast.success(`${assignment.type === "individual" ? "Individual" : "Sub contractor"} assigned successfully`);
    } catch (error) {
      toast.error(fieldActionMessage(error, `Failed to assign ${assignment.type}`));
      throw error;
    } finally {
      setAssigningResource(false);
    }
  };

  const unassigning = useRef(false);

  const handleUnassignResource = async () => {
    if (unassigning.current) return;
    unassigning.current = true;
    setAssigningResource(true);
    try {
      const updatedAppointment = await unassignAppointment(appointment.id);
      onAppointmentUpdate?.(updatedAppointment);
      toast.success("Resource unassigned successfully");
    } catch (error) {
      toast.error(fieldActionMessage(error, "Failed to unassign resource"));
    } finally {
      unassigning.current = false;
      setAssigningResource(false);
    }
  };

  const hasAssignment = !!appointment.assigned_user.name || !!appointment.assigned_organization?.id;
  const canAssignResources = appointment.status === appointment_status.pending || appointment.status === appointment_status.rescheduled;
  const canUnassignResource = isUnassignable(appointment.status as appointment_status, hasAssignment);

  return {
    assigningResource,
    handleAssignResource,
    handleUnassignResource,
    canAssignResources,
    canUnassignResource,
    hasAssignment,
  };
}
